import { useUsersQuery } from "./use-users-query";
import type { TellescopeModelFromName } from "../../mappings";

/**
 * Fetches a single user by ID from the Tellescope API.
 *
 * This is a convenience hook that wraps `useUsersQuery` to simplify
 * fetching a single user record. It automatically extracts the first
 * result from the query.
 *
 * @param userId - The ID of the user to fetch. If empty/falsy, the query
 *                 is disabled and returns undefined.
 * @param enabled - Optional boolean to enable/disable query. Defaults to enabled.
 *
 * @returns The user record if found, or undefined if:
 *   - The userId is empty/falsy
 *   - The query is still loading
 *   - The user doesn't exist
 *
 * @example
 * ```tsx
 * // Fetch a single user
 * const user = useUser('user123');
 * if (user) {
 *   console.log(user.name);
 * }
 *
 * // Disabled when userId is empty
 * const noUser = useUser(''); // Returns undefined, query disabled
 * ```
 *
 * @remarks
 * - Internally uses `useUsersQuery` with a single-item array
 * - Query is disabled when userId is falsy to prevent unnecessary API calls
 * - Returns the first matching user from the query results
 */
export const useUser = (userId: string, enabled: boolean = true): TellescopeModelFromName<"users"> | undefined => {
  const query = useUsersQuery(userId ? [userId] : [], enabled);
  return query.data?.[0];
};
